'use client';

import { Suspense, useEffect, useRef, useState } from 'react';
import { AisLink as Link, useAisRuDomen } from '@/components/aisearch/ais-link';
import type { GruppaSoSsylkami } from '@/lib/ais-podval-ssylki';
import Image from 'next/image';
import { usePublicPathname } from '@/lib/use-public-pathname';
import { useLocale, useTranslations } from 'next-intl';
import { localesWithPosts } from '@/lib/posts';
import { ThemeSwitcher } from '@/components/ui/theme-switcher';
import { AccountMenu } from '@/components/layout/account-menu';
import { SupportButton } from '@/components/aisearch/support-button';
import { SharedSearchFormIzAdresa } from '@/components/aisearch/shared-search-form';
import { AISEARCH_HOME } from '@/lib/account-access';
import { Menu, X } from 'lucide-react';
import type { Instrument } from '@/lib/poisk-podskazki';

/**
 * Шапка AiSearch.
 *
 * Устроена как у INGDG и DataHunter: логотип слева, переключатель темы и
 * вход справа. Переключателя языка здесь нет — он живёт в подвале, как у
 * DataHunter, чтобы шапка оставалась короткой на телефоне.
 *
 * Стоимость вынесена из подвала в шапку, но только на русском домене.
 * Причина не в оформлении: на .tech тарифы назначены в долларах и евро и
 * продуктом не подтверждены, а пункт в шапке — это приглашение платить.
 * В подвале ссылка остаётся на обоих доменах: до неё доходят те, кто
 * ищет цену намеренно.
 *
 * Пункт один, и на узком экране он всё равно уезжает в гамбургер: иначе
 * при появлении второго пункта пришлось бы переделывать шапку заново, а
 * рядом с кнопкой входа лишняя ссылка сжимает её на телефоне.
 */
/** Языки .tech, на которых в блоге есть статьи (ru блога на .tech нет). */
const JAZYKI_BLOGA_TECH: readonly string[] = localesWithPosts('aisearch').filter((l) => l !== 'ru');

export function AiSearchHeader({ gruppy }: { gruppy: GruppaSoSsylkami[] }) {
  const t = useTranslations('ais_layout');
  const locale = useLocale();
  // Домен известен уже на сервере: признак приходит из layout витрины,
  // как и в подвале. Определять его на клиенте нельзя — тогда в готовом
  // HTML остаётся разметка чужого домена.
  const ruDomen = useAisRuDomen();
  const [open, setOpen] = useState(false);
  /**
   * Раскрыто ли меню инструментов на широком экране.
   *
   * Отдельно от `open`, который отвечает за бургер на узком: это разные
   * меню с разной разметкой, и общее состояние закрывало бы одно при
   * открытии другого.
   */
  const [instrumenty, setInstrumenty] = useState(false);
  const pathname = usePublicPathname();
  // Инструменты для подсказок поиска: те же ссылки, что в меню.
  const instrumentyPoiska: Instrument[] = gruppy.flatMap((g) =>
    g.ssylki.map((s) => ({ href: s.href, name: t(s.klyuchPodpisi) })),
  );

  /**
   * Отсрочка закрытия меню инструментов.
   *
   * Кнопка и карточка со ссылками лежат в разных ветках разметки, и
   * между ними курсор неизбежно оказывается вне обеих. Без отсрочки
   * меню закрывалось тем же кадром, а закрытие вешает на блок класс
   * hidden: скрытый блок событий мыши не получает и вернуть себя уже
   * не может. Дойти от кнопки до ссылок было невозможно.
   *
   * Отсрочка только на закрытии. Задержка на открытии читалась бы как
   * тормоза интерфейса.
   */
  const zakrytie = useRef<ReturnType<typeof setTimeout> | null>(null);

  const otkryt = () => {
    if (zakrytie.current !== null) {
      clearTimeout(zakrytie.current);
      zakrytie.current = null;
    }
    setInstrumenty(true);
  };

  const zakryt = (srazu = false) => {
    if (zakrytie.current !== null) clearTimeout(zakrytie.current);

    if (srazu) {
      zakrytie.current = null;
      setInstrumenty(false);
      return;
    }

    // Четверть секунды: столько занимает путь курсора от кнопки к
    // списку. Больше — меню висит за ушедшим человеком, меньше — снова
    // не успеть.
    zakrytie.current = setTimeout(() => {
      setInstrumenty(false);
      zakrytie.current = null;
    }, 250);
  };

  // Отложенное закрытие не должно срабатывать после ухода со страницы:
  // иначе React ругается на обновление размонтированного дерева.
  useEffect(
    () => () => {
      if (zakrytie.current !== null) clearTimeout(zakrytie.current);
    },
    [],
  );

  /**
   * Меню закрывается при уходе на другую страницу.
   *
   * Переход внутри приложения не перезагружает документ, и без этого
   * раскрытое меню висело бы поверх новой страницы. Та же причина, что
   * и в шапке DataHunter.
   */
  useEffect(() => {
    setOpen(false);
    // Сразу, без отсрочки: страница уже сменилась, ждать нечего.
    if (zakrytie.current !== null) clearTimeout(zakrytie.current);
    zakrytie.current = null;
    setInstrumenty(false);
  }, [pathname]);

  /**
   * Меню закрывается по Escape и по щелчку мимо.
   *
   * Без Escape человек, открывший меню с клавиатуры, оказывается в
   * ловушке: закрыть его нечем, а табом он уйдёт внутрь списка.
   */
  useEffect(() => {
    if (!instrumenty) return;

    const poEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') zakryt(true);
    };

    document.addEventListener('keydown', poEscape);
    return () => {
      document.removeEventListener('keydown', poEscape);
    };
  }, [instrumenty]);

  // Фон шапки сплошной: полупрозрачная шапка под фоновым плеером
  // просвечивала текстом страницы у верхнего края, и это читалось как
  // щель между плеером и шапкой (владелец, 29.10.2026).
  return (
    <header className="sticky top-0 lg:top-[var(--fon-pleer-verh,0px)] z-50 border-b border-[var(--color-border)] bg-[var(--color-bg)]">
      <div className="max-w-[1280px] mx-auto px-6 h-16 flex items-center justify-between">
        {/*
          Меню лежит в одной группе с логотипом, а не отдельным блоком.
          Три блока внутри justify-between растаскиваются по краям и
          центру, и единственный пункт вставал посреди пустой шапки —
          читался как заголовок страницы, а не как ссылка рядом с
          названием продукта.
        */}
        <div className="flex items-center gap-3">
          {ruDomen && (
            <button
              onClick={() => {
                setOpen(!open);
              }}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg border border-[var(--color-border)] hover:border-[var(--color-text-muted)] transition-colors"
              // Гамбургер, а не лупа: лупа на месте меню путала — её
              // принимали за поиск и не находили разделов (владелец,
              // 30.10.2026). Поиск остался первой строкой в меню.
              aria-label={t('menu')}
              aria-expanded={open}
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          )}

          {/* Без предзагрузки (#213): на главной это ссылка на саму себя, и
              Next запрашивал её данные повторно до первой отрисовки. */}
          <Link
            href="/"
            prefetch={false}
            className="flex shrink-0 items-center gap-2 font-bold text-lg"
          >
            {/* Оригинальный значок с aisearch.ru, а не собственная отрисовка:
                бренд узнаётся по конкретному начертанию «ai», подделка под
                него читалась бы как чужой, непохожий логотип. */}
            <Image
              src="/icons/aisearch/logo-115.png"
              alt="AiSearch"
              width={32}
              height={32}
              className="h-8 w-8"
              priority
            />
            {/* Слова «AiSearch» рядом больше нет (правка владельца 28.09):
                логотип и есть имя, надпись его дублировала. Для читалок
                имя остаётся в alt. */}
          </Link>

          {ruDomen && (
            <nav className="hidden md:flex items-center gap-2 text-sm ml-3">
              {/*
                Инструменты выпадающим меню, а не списком в строку: их
                четырнадцать, и в строку они не помещаются.

                Ссылки лежат в разметке всегда и прячутся стилем, а не
                условием: условный рендер убрал бы их из отданного HTML,
                и до запуска скриптов их не было бы ни для поисковика,
                ни для читалки. Тот же приём, что в меню узкого экрана
                ниже.
              */}
              <div
                className="relative"
                onMouseEnter={otkryt}
                onMouseLeave={() => {
                  zakryt();
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    if (instrumenty) zakryt(true);
                    else otkryt();
                  }}
                  aria-expanded={instrumenty}
                  aria-haspopup="true"
                  // Связывает кнопку с блоком, которым она управляет.
                  // Без этого читалка объявляет «раскрыто», но не
                  // говорит, что именно раскрылось.
                  aria-controls="ais-instrumenty"
                  className="px-3 py-2 rounded-lg text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)] transition-colors"
                >
                  {t('tools')}
                </button>
              </div>

              {/* Раздел для бизнеса (#187): простая ссылка между
                  инструментами и стоимостью, как просил владелец.
                  Подсвечен на всех страницах раздела, не только на корне. */}
              <Link
                href="/business"
                aria-current={pathname.startsWith('/business') ? 'page' : undefined}
                className={
                  pathname.startsWith('/business')
                    ? 'px-3 py-2 rounded-lg transition-colors text-[var(--color-text)] font-medium'
                    : 'px-3 py-2 rounded-lg transition-colors text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)]'
                }
              >
                {t('nav_business')}
              </Link>

              {/* Раздел «Программы»: наш софт для компьютера. Этот блок шапки
                  и так виден только на русской витрине. */}
              <Link
                href="/programmy"
                aria-current={pathname.startsWith('/programmy') ? 'page' : undefined}
                className={
                  pathname.startsWith('/programmy')
                    ? 'px-3 py-2 rounded-lg transition-colors text-[var(--color-text)] font-medium'
                    : 'px-3 py-2 rounded-lg transition-colors text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)]'
                }
              >
                {t('nav_programmy')}
              </Link>

              <Link
                href="/pricing"
                // Текущий раздел подсвечен: иначе непонятно, открыта ли
                // уже та страница, на которую ведёт ссылка.
                aria-current={pathname === '/pricing' ? 'page' : undefined}
                className={`px-3 py-2 rounded-lg transition-colors ${
                  pathname === '/pricing'
                    ? 'text-[var(--color-text)] font-medium'
                    : 'text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)]'
                }`}
              >
                {t('pricing')}
              </Link>
            </nav>
          )}

          {/* На aisearch.tech меню не было вовсе, и на программы не вело
              ни одной ссылки (просьба владельца 04.10.2026). Две ссылки помещаются
              и на телефоне, блог — с 640 px, поэтому без отдельного меню. */}
          {!ruDomen && (
            <nav className="ml-2 flex items-center text-sm">
              <Link
                href="/programs"
                aria-current={pathname.startsWith('/programs') ? 'page' : undefined}
                className={`px-2 py-2 rounded-lg transition-colors sm:px-3 ${
                  pathname.startsWith('/programs')
                    ? 'text-[var(--color-text)] font-medium'
                    : 'text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)]'
                }`}
              >
                {t('nav_programmy')}
              </Link>
              {/* Блог .tech (06.10.2026, ТЗ Seo-marketing): между программами
                  и ценами. Подпись та же, что в подвале. */}
              {JAZYKI_BLOGA_TECH.includes(locale) && (
                <Link
                  href="/blog"
                  aria-current={pathname.startsWith('/blog') ? 'page' : undefined}
                  // На телефоне третья ссылка не помещается: на es шапка
                  // вылезала за 375 px, а логотип сжимался в ноль. Там блог
                  // ведёт из подвала, в шапке он с 640 px.
                  className={`hidden px-2 py-2 rounded-lg transition-colors sm:inline-block sm:px-3 ${
                    pathname.startsWith('/blog')
                      ? 'text-[var(--color-text)] font-medium'
                      : 'text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)]'
                  }`}
                >
                  {t('footer_blog')}
                </Link>
              )}
              <Link
                href="/pricing"
                aria-current={pathname === '/pricing' ? 'page' : undefined}
                className={`px-2 py-2 rounded-lg transition-colors sm:px-3 ${
                  pathname === '/pricing'
                    ? 'text-[var(--color-text)] font-medium'
                    : 'text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)]'
                }`}
              >
                {t('pricing')}
              </Link>
            </nav>
          )}
        </div>

        {/*
          Поиск по готовым работам в свободном месте шапки, между меню и
          значками (просьба владельца 26.10.2026), на всех страницах
          (29.10.2026; раньше только в разделе /shared). Только с 1024 px:
          уже места нет, и на /shared та же форма стоит вверху страницы.
          Без Suspense useSearchParams сделал бы клиентскими все страницы
          с этой шапкой.
        */}
        <div className="hidden min-w-0 flex-1 justify-end px-4 lg:flex">
          <Suspense fallback={null}>
            <SharedSearchFormIzAdresa
              className="w-full max-w-xs xl:max-w-sm"
              instrumenty={instrumentyPoiska}
            />
          </Suspense>
        </div>

        <div className="flex items-center gap-3">
          <ThemeSwitcher />
          {/* Кабинет общий с INGDG, но на этом домене его главная закрыта,
              поэтому ведём сразу к файлам, как у DataHunter. */}
          <AccountMenu home={AISEARCH_HOME} />
          {/* Поддержка после входа, а не перед ним: вход — то, зачем
              приходят, а поддержка нужна, когда что-то не получилось. */}
          <SupportButton />
        </div>
      </div>

      {/*
        Меню узкого экрана отрисовано всегда и прячется стилем, а не
        условием в разметке. Условный рендер убирал бы ссылку из отданного
        HTML, и до запуска скриптов её не было бы ни для поисковика, ни
        для читалки. Проверено на шапке DataHunter: там этим потеряли всю
        перелинковку инструментов.
      */}
      {/*
        Список инструментов в одном экземпляре на всю шапку.
        
        Раньше он лежал дважды: копия в выпадающем меню широкого экрана
        и копия здесь, в бургере. В HTML попадали обе — 31 ссылка вместо
        16, и лишние 8 КБ разметки на каждой странице сайта. Вес ссылкам
        это не добавляло: при двух ссылках на одну страницу Google
        учитывает анкор первой.
        
        Теперь блок один и меняет раскладку по ширине экрана: на широком
        всплывает под кнопкой «Инструменты», на узком раскрывается
        бургером на всю ширину. Виден при любом из двух раскрытий.
      */}
      {ruDomen && (
        <div
          id="ais-instrumenty"
          /*
            На узком экране это сама панель: во всю ширину, с фоном и
            рамкой. Высота ограничена экраном под шапкой, а лишнее
            листается внутри: шапка прилипает к верху, и без этого
            нижние ссылки уходили за край, куда не долистать. На широком — прозрачная обёртка с тем же
            контейнером, что у шапки, а видимую карточку рисует список
            внутри.

            Обёртка нужна, чтобы карточка встала под кнопкой, а не у
            края окна. Первая попытка снимала left/right целиком
            (md:left-auto), и блок растянулся от левого края экрана
            поверх текста страницы.

            Мышь на широком экране обёртка не ловит: она накрывает всю
            полосу под шапкой и перехватывала бы клики по странице.
          */
          className={`absolute top-full left-0 right-0 z-40 max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain border-t border-[var(--color-border)] bg-[var(--color-bg)] shadow-2xl md:max-h-none md:overflow-visible md:border-t-0 md:bg-transparent md:shadow-none md:pointer-events-none ${
            open || instrumenty ? 'block' : 'hidden'
          }`}
          onMouseEnter={otkryt}
          onMouseLeave={() => {
            zakryt();
          }}
        >
          <nav className="flex flex-col px-6 py-4 md:mx-auto md:max-w-[1280px] md:flex-row md:gap-8 md:px-6 md:py-0">
            {/*
              Отступ от шапки задан внутренним полем карточки
              (md:pt-2 при прозрачном фоне), а не отступом обёртки.
              Обёртка мышь не ловит, и будь зазор её отступом, курсор,
              идущий от кнопки вниз, попадал бы в мёртвую полосу: оба
              обработчика наведения молчат, и меню закрывается прямо
              под рукой.
            */}
            <div className="md:pointer-events-auto md:flex md:gap-8 md:pt-2">
              <div className="md:flex md:gap-8 md:rounded-xl md:border md:border-[var(--color-border)] md:bg-[var(--color-bg)] md:p-6 md:shadow-2xl">
                {/* Поиск первой строкой меню на телефоне: в шапке ему места
                нет, а со страницы /shared он переехал сюда (владелец,
                29.10.2026). Без автофокуса: меню открывают ради разделов,
                и выскочившая клавиатура закрывала бы их. */}
                {open && (
                  <div className="mb-3 md:hidden">
                    <Suspense fallback={null}>
                      <SharedSearchFormIzAdresa
                        krupno
                        instrumenty={instrumentyPoiska}
                        onPereshod={() => {
                          setOpen(false);
                        }}
                      />
                    </Suspense>
                  </div>
                )}
                {/* Стоимость только в бургере: на широком экране она стоит
                отдельным пунктом в самой шапке. */}
                <Link
                  href="/pricing"
                  onClick={() => {
                    setOpen(false);
                  }}
                  aria-current={pathname === '/pricing' ? 'page' : undefined}
                  className="px-3 py-2 rounded-lg hover:bg-[var(--color-bg-elevated)] text-[var(--color-text)] transition-colors md:hidden"
                >
                  {t('pricing')}
                </Link>
                <Link
                  href="/business"
                  onClick={() => {
                    setOpen(false);
                  }}
                  aria-current={pathname.startsWith('/business') ? 'page' : undefined}
                  className="px-3 py-2 rounded-lg hover:bg-[var(--color-bg-elevated)] text-[var(--color-text)] transition-colors md:hidden"
                >
                  {t('nav_business')}
                </Link>
                <Link
                  href="/programmy"
                  onClick={() => {
                    setOpen(false);
                  }}
                  aria-current={pathname.startsWith('/programmy') ? 'page' : undefined}
                  className="px-3 py-2 rounded-lg hover:bg-[var(--color-bg-elevated)] text-[var(--color-text)] transition-colors md:hidden"
                >
                  {t('nav_programmy')}
                </Link>

                {/* На узком экране группы идут подряд, ссылки группы в две
                колонки: одной колонкой список не помещался в экран. На
                широком группы стоят колонками, ссылки в столбик. */}
                {gruppy.map(({ klyuchZagolovka, ssylki }) => {
                  return (
                    <div key={klyuchZagolovka} className="mt-4 md:mt-0 md:min-w-[160px]">
                      <p className="px-3 pb-1 text-xs font-bold uppercase tracking-wider text-[var(--color-accent-400)] md:px-0 md:pb-2">
                        {t(klyuchZagolovka)}
                      </p>
                      <div className="grid grid-cols-2 gap-x-2 md:block">
                        {ssylki.map((ssylka) => (
                          <Link
                            key={ssylka.href}
                            href={ssylka.href}
                            onClick={() => {
                              setOpen(false);
                              setInstrumenty(false);
                            }}
                            aria-current={pathname === ssylka.href ? 'page' : undefined}
                            className="block px-3 py-2 rounded-lg text-sm text-[var(--color-text-muted)] md:text-base transition-colors hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text)] md:whitespace-nowrap md:rounded-none md:px-0 md:py-1 md:hover:bg-transparent md:hover:text-[var(--color-accent-400)]"
                          >
                            {t(ssylka.klyuchPodpisi)}
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
